import { roundedRect } from '@bn-games/render-helpers';
import type { Pip, Tile } from '../game/types.js';
import {
  PIP_RADIUS,
  TILE_CORNER_RADIUS,
  TILE_DIVIDER_WIDTH,
  TILE_HOVER_GLOW_BLUR,
  TILE_SHORT,
} from '../utils/constants.js';
import type { RenderPalette } from './palette.js';

/**
 * Scale factor implied by a rect's short side relative to the base tile.
 * Lets interior features (corner radius, pip radius, divider thickness)
 * grow in lockstep with rack-tile inflation without each callsite passing
 * a scale parameter through.
 */
function tileScale(rect: TileRect): number {
  return rect.short / TILE_SHORT;
}

/**
 * Where on the canvas to render a tile, and which half is "first". When
 * `orientation` is `horizontal` the long axis runs left→right and `halfA`
 * paints in the left half, `halfB` in the right. When `vertical`, the long
 * axis runs top→bottom and `halfA` paints in the top half, `halfB` in the
 * bottom. The renderer is purely geometric — chain-layout logic decides
 * which physical pip goes in halfA vs halfB and feeds those values here.
 */
export interface TileRect {
  /** Top-left corner of the tile's bounding box. */
  readonly x: number;
  readonly y: number;
  /** Long-axis dimension in px. */
  readonly long: number;
  /** Short-axis dimension in px. */
  readonly short: number;
  readonly orientation: 'horizontal' | 'vertical';
}

export interface DrawTileOptions {
  /** Add a soft glow around the tile. */
  readonly hover?: boolean;
  /** Add an outline ring (used for the currently-selected tile in hand). */
  readonly selected?: boolean;
  /** Fade the tile (used for ghost previews / disabled tiles). */
  readonly alpha?: number;
}

export function drawTileFace(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  halfA: Pip,
  halfB: Pip,
  palette: RenderPalette,
  options: DrawTileOptions = {},
): void {
  ctx.save();
  if (options.alpha !== undefined && options.alpha < 1) {
    ctx.globalAlpha = options.alpha;
  }

  const { x, y, width, height } = rectBounds(rect);
  const scale = tileScale(rect);
  const cornerRadius = TILE_CORNER_RADIUS * scale;

  if (options.hover) {
    ctx.shadowColor = palette.hoverGlow;
    ctx.shadowBlur = TILE_HOVER_GLOW_BLUR;
  }

  // Drop shadow under the tile — drawn as an inset darker rect so the
  // shadow doesn't bleed beyond the tile's footprint into the chain.
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;
  roundedRect(ctx, x, y, width, height, cornerRadius);
  ctx.fillStyle = palette.tileFaceBase;
  ctx.fill();
  ctx.restore();

  // Glossy gradient over the face — direction follows the long axis.
  const grad =
    rect.orientation === 'horizontal'
      ? ctx.createLinearGradient(x, y, x, y + height)
      : ctx.createLinearGradient(x, y, x + width, y);
  grad.addColorStop(0, palette.tileFaceHighlight);
  grad.addColorStop(0.55, palette.tileFaceBase);
  grad.addColorStop(1, palette.tileFaceShadow);
  ctx.fillStyle = grad;
  roundedRect(ctx, x, y, width, height, cornerRadius);
  ctx.fill();

  // Border.
  ctx.strokeStyle = palette.tileFaceBorder;
  ctx.lineWidth = 1;
  roundedRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, cornerRadius);
  ctx.stroke();

  // Divider line splitting the two halves.
  ctx.strokeStyle = palette.tileDivider;
  ctx.lineWidth = TILE_DIVIDER_WIDTH * scale;
  ctx.beginPath();
  const dividerInset = 3 * scale;
  if (rect.orientation === 'horizontal') {
    const mid = x + width / 2;
    ctx.moveTo(mid, y + dividerInset);
    ctx.lineTo(mid, y + height - dividerInset);
  } else {
    const mid = y + height / 2;
    ctx.moveTo(x + dividerInset, mid);
    ctx.lineTo(x + width - dividerInset, mid);
  }
  ctx.stroke();

  // Pips for each half.
  const { halfA: halfABounds, halfB: halfBBounds } = halfBounds(rect);
  const pipRadius = PIP_RADIUS * scale;
  drawPips(ctx, halfABounds, halfA, palette, pipRadius);
  drawPips(ctx, halfBBounds, halfB, palette, pipRadius);

  if (options.selected) {
    ctx.strokeStyle = palette.selectedRing;
    ctx.lineWidth = 2.5;
    roundedRect(ctx, x - 1.5, y - 1.5, width + 3, height + 3, cornerRadius + 2);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draws a face-down tile (opponent hands). Same geometry as a face tile but
 * no pips — a slim diagonal pattern over the wood back.
 */
export function drawTileBack(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  palette: RenderPalette,
  options: DrawTileOptions = {},
): void {
  ctx.save();
  if (options.alpha !== undefined && options.alpha < 1) {
    ctx.globalAlpha = options.alpha;
  }

  const { x, y, width, height } = rectBounds(rect);
  const scale = tileScale(rect);
  const cornerRadius = TILE_CORNER_RADIUS * scale;

  // Shadow.
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1.5;
  roundedRect(ctx, x, y, width, height, cornerRadius);
  ctx.fillStyle = palette.tileBackBase;
  ctx.fill();
  ctx.restore();

  // Wood-back gradient.
  const grad =
    rect.orientation === 'horizontal'
      ? ctx.createLinearGradient(x, y, x, y + height)
      : ctx.createLinearGradient(x, y, x + width, y);
  grad.addColorStop(0, palette.tileBackPattern);
  grad.addColorStop(0.5, palette.tileBackBase);
  grad.addColorStop(1, palette.tileBackPattern);
  ctx.fillStyle = grad;
  roundedRect(ctx, x, y, width, height, cornerRadius);
  ctx.fill();

  // Inner inset rectangle as a faux-inlay.
  ctx.strokeStyle = palette.tileBackPattern;
  ctx.lineWidth = 1;
  const inset = 4 * scale;
  roundedRect(ctx, x + inset, y + inset, width - inset * 2, height - inset * 2, 2 * scale);
  ctx.stroke();

  ctx.strokeStyle = palette.tileBackBorder;
  ctx.lineWidth = 1;
  roundedRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, cornerRadius);
  ctx.stroke();

  ctx.restore();
}

/**
 * Convenience helper for the layout pass: pick the (halfA, halfB) ordering
 * for a non-double tile played onto an arm whose `openValue` faces back
 * toward the chain. `tile[0]` is the canonical low pip.
 *
 * @returns [innerHalf, outerHalf] — `innerHalf` faces the chain (matches
 *   the arm's open value before the play), `outerHalf` becomes the new
 *   open value.
 */
export function orientNonDouble(tile: Tile, matchValue: Pip): [Pip, Pip] {
  if (tile[0] === matchValue) return [tile[0], tile[1]];
  return [tile[1], tile[0]];
}

/** Bounding-box mapping from `TileRect` (long/short axes) to canvas coords. */
export function rectBounds(rect: TileRect): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (rect.orientation === 'horizontal') {
    return { x: rect.x, y: rect.y, width: rect.long, height: rect.short };
  }
  return { x: rect.x, y: rect.y, width: rect.short, height: rect.long };
}

interface HalfRect {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

function halfBounds(rect: TileRect): { halfA: HalfRect; halfB: HalfRect } {
  const { x, y, width, height } = rectBounds(rect);
  if (rect.orientation === 'horizontal') {
    const size = height;
    return {
      halfA: { x, y, size },
      halfB: { x: x + width - size, y, size },
    };
  }
  const size = width;
  return {
    halfA: { x, y, size },
    halfB: { x, y: y + height - size, size },
  };
}

function drawPips(
  ctx: CanvasRenderingContext2D,
  half: HalfRect,
  pip: Pip,
  palette: RenderPalette,
  pipRadius: number,
): void {
  const positions = pipPositions(pip);
  if (positions.length === 0) return;
  for (const [fx, fy] of positions) {
    const cx = half.x + fx * half.size;
    const cy = half.y + fy * half.size;
    // Soft shadow behind each pip for depth.
    ctx.fillStyle = palette.pipShadow;
    ctx.beginPath();
    ctx.arc(cx + 0.5, cy + 1, pipRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = palette.pipColor;
    ctx.beginPath();
    ctx.arc(cx, cy, pipRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Pip positions are fractions of a half-square (0..1 across each axis).
const CENTER: readonly [number, number] = [0.5, 0.5];
const TL: readonly [number, number] = [0.28, 0.28];
const TR: readonly [number, number] = [0.72, 0.28];
const BL: readonly [number, number] = [0.28, 0.72];
const BR: readonly [number, number] = [0.72, 0.72];
const ML: readonly [number, number] = [0.28, 0.5];
const MR: readonly [number, number] = [0.72, 0.5];

const PIP_PATTERNS: readonly (readonly (readonly [number, number])[])[] = [
  [],
  [CENTER],
  [TL, BR],
  [TL, CENTER, BR],
  [TL, TR, BL, BR],
  [TL, TR, CENTER, BL, BR],
  [TL, TR, ML, MR, BL, BR],
];

function pipPositions(pip: Pip): readonly (readonly [number, number])[] {
  return PIP_PATTERNS[pip] ?? [];
}
